import React__default from 'react';
import styled from 'styled-components';
import { AkChevronDownIcon } from '@fuegokit/fuegoicons-react';
import { themeGet } from '../../utils/theme-get.js';
import 'react-stately';
import { useFilter } from 'react-aria';
import '@react-aria/ssr';
import Combobox from '../Combobox/Combobox.js';
import Box from '../Box/Box.js';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden.js';
import Text from '../Text/Text.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

// InnerVisualSlot

/**
 * Inner visual slot to house leading visual, trailing visual, counter, and loading spinners
 */
const InnerVisualSlot = ({
  children
}) => {
  // just render a visual
  return /*#__PURE__*/React__default.createElement("span", {
    className: "combobox-item-visual"
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }
  }, children));
};
InnerVisualSlot.displayName = "InnerVisualSlot";
const fontSize = () => 14;
const CustomItem = ({
  UNSAFE_className,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Combobox.Item, _extends({
    className: UNSAFE_className
  }, props));
};
CustomItem.displayName = "CustomItem";
const StyledActionsFilter = styled(Combobox).withConfig({
  displayName: "ActionsFilter__StyledActionsFilter",
  componentId: "sc-u0hrcy-0"
})(["[slot='description']{font-size:12px;}[slot='errorMessage']{font-size:12px;color:var(--ds-text-danger);}"]);
styled(CustomItem).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterItem",
  componentId: "sc-u0hrcy-1"
})(["padding:", " 0;outline:none;cursor:default;color:", ";font-size:", ";position:relative;width:100%;padding:6px 12px 6px 16px;&:focus{background:", ";color:", ";}&:hover{background:", ";color:", ";}&[data-focus-visible]{background:", ";color:", ";}&[aria-selected='true']{background-color:", ";color:", ";font-weight:600;box-shadow:inset 2px 0px 0px ", ";&:hover{background:", ";color:", ";}&::before{alt:' ';position:absolute;top:6px;left:2px;}}&[data-pressed]{background-color:", ";color:", ";}&[aria-disabled]{background-color:", ";color:", ";cursor:not-allowed;}@media (forced-colors:active){&:focus{background:Highlight;color:HighlightText;}}"], themeGet('space.2'), themeGet('colors.text.default'), themeGet('fontSizes.3'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.selected.default.hovered'), themeGet('colors.text.selected'), themeGet('colors.border.selected'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.neutral.subtle.pressed'), themeGet('colors.text.default'), themeGet('colors.elevation.surface.default.[default]'), themeGet('colors.text.disabled'));
const StyledActionsFilterPopover = styled(Combobox.Popover).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterPopover",
  componentId: "sc-u0hrcy-2"
})(["--background-color:", ";--border-color:", ";border:1px solid var(--border-color);width:var(--trigger-width);box-sizing:border-box;box-shadow:", ";background:var(--background-color);outline:none;border-radius:", ";"], themeGet('colors.elevation.surface.default.[default]'), themeGet('colors.border.default'), themeGet('shadows.elevation.shadow.overlay'), themeGet('radii.1'));
const StyledActionsFilterListbox = styled(Combobox.Listbox).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterListbox",
  componentId: "sc-u0hrcy-3"
})(["--highlight-background:$themeGet('colors.background.neutral.subtle.hovered');--highlight-foreground:", ";--text-color:", ";--text-color-disabled:", ";max-height:inherit;box-sizing:border-box;overflow:auto;outline:none;padding:", " 0;"], themeGet('colors.text.inverse'), themeGet('colors.text.default'), themeGet('colors.text.disabled'), themeGet('space.2'));
const StyledActionsFilterInput = styled(Combobox.Input).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterInput",
  componentId: "sc-u0hrcy-4"
})(["vertical-align:middle;line-height:", ";align-items:center;border-radius:", ";border-width:2;border-style:solid;display:flex;flex:1;width:100%;font-size:", ";justify-content:space-between;overflow:hidden;transition:background-color 0.2s ease-in-out,border-color 0.2s ease-in-out;pointer-events:auto;padding:", " 12px;cursor:text;background-color:", ";border-color:transparent;color:inherit;&[aria-invalid]{border-color:", ";}&:focus{outline:none;border-color:", ";box-shadow:0 0 0 1px ", ";&[aria-invalid]{border-color:", ";box-shadow:0 0 0 1px ", ";}}"], 8 * 2.5 / fontSize(), themeGet('radii.1'), themeGet('fontSizes.3'), themeGet('space.2'), themeGet('colors.background.neutral.default.[default]'), themeGet('colors.border.danger'), themeGet('colors.border.focused'), themeGet('colors.border.focused'), themeGet('colors.border.danger'), themeGet('colors.border.danger'));
const StyledActionsFilterButton = styled(Combobox.Button).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterButton",
  componentId: "sc-u0hrcy-5"
})(["appearance:none;margin:0;padding:0;border:none;background:transparent;vertical-align:middle;cursor:pointer;text-align:left;font:inherit;user-select:none;align-items:center;color:inherit;margin-left:-2.48rem;font-weight:normal;font-size:", ";text-decoration:none;opacity:0.85;font-weight:", ";padding:", " 8px;border-radius:", ";&:hover,&:focus{opacity:1;text-decoration:none;}&:active{opacity:1;text-decoration:none;background-color:", ";color:", ";}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";}svg{transition:transform 0.15s ease;}&[aria-expanded='true']{--icon-rotation:180deg;color:", ";svg{transform:rotate(var(--icon-rotation));}}"], themeGet('fontSizes.3'), themeGet('fontWeights.medium'), themeGet('space.2'), themeGet('radii.1'), themeGet('colors.background.selected.default.[default]'), themeGet('colors.text.selected'), themeGet('colors.border.focused'), themeGet('colors.border.focused'), themeGet('colors.text.selected'));
styled(Combobox.Header).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterHeader",
  componentId: "sc-u0hrcy-6"
})(["font-size:", ";font-weight:", ";padding:", " ", ";"], themeGet('fontSizes.4'), themeGet('fontWeights.medium'), themeGet('space.2'), themeGet('space.3'));
styled(Combobox.Section).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterSection",
  componentId: "sc-u0hrcy-7"
})(["&:not(:first-child){margin-top:12px;}"]);
const StyledActionsFilterText = styled(Combobox.Text).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterText",
  componentId: "sc-u0hrcy-8"
})(["&[slot='label']{font-size:", ";font-weight:", ";}&[slot='errorMessage']{font-size:", ";color:var(--ds-text-danger);}&[slot='description']{font-size:", ";}&[slot='description']{&[aria-selected='true']{color:", ";}}"], themeGet('fontSizes.3'), themeGet('fontWeights.semibold'), themeGet('fontSizes.1'), themeGet('fontSizes.1'), themeGet('colors.text.selected'));
styled(Combobox.Label).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterLabel",
  componentId: "sc-u0hrcy-9"
})(["&[slot='label']{}"]);
styled(Box).withConfig({
  displayName: "ActionsFilter__StyledActionsFilterSeparator",
  componentId: "sc-u0hrcy-10"
})(["height:1px;background-color:", ";margin:", ";"], themeGet('colors.border.default'), themeGet('space.2'));
const ComplexComboboxItem = ({
  UNSAFE_className,
  leadingVisual: LeadingVisual,
  children,
  label,
  description,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Combobox.Item, _extends({
    className: UNSAFE_className
  }, props), /*#__PURE__*/React__default.createElement(InnerVisualSlot, null, typeof LeadingVisual === 'function' ? /*#__PURE__*/React__default.createElement(LeadingVisual, null) : LeadingVisual), children, label && description ? /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }
  }, /*#__PURE__*/React__default.createElement(StyledActionsFilterText, {
    slot: "label"
  }, label), /*#__PURE__*/React__default.createElement(StyledActionsFilterText, {
    slot: "description"
  }, description)) : null);
};
ComplexComboboxItem.displayName = "ComplexComboboxItem";
const StyledComplexActionsFilterItem = styled(ComplexComboboxItem).withConfig({
  displayName: "ActionsFilter__StyledComplexActionsFilterItem",
  componentId: "sc-u0hrcy-11"
})(["padding:", " 0;outline:none;cursor:default;color:", ";font-size:", ";position:relative;width:100%;padding:6px 12px 6px 16px;display:flex;align-items:center;gap:4px;&:focus{background:", ";color:", ";}&:hover{background:", ";color:", ";}&[data-focus-visible]{background:", ";color:", ";}&[aria-selected='true']{background-color:", ";color:", ";font-weight:600;box-shadow:inset 2px 0px 0px ", ";&:hover{background:", ";color:", ";}&::before{alt:' ';position:absolute;top:6px;left:2px;}}&[data-pressed]{background-color:", ";color:", ";}&[aria-disabled]{background-color:", ";color:", ";cursor:not-allowed;}@media (forced-colors:active){&:focus{background:Highlight;color:HighlightText;}}"], themeGet('space.2'), themeGet('colors.text.default'), themeGet('fontSizes.3'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.selected.default.hovered'), themeGet('colors.text.selected'), themeGet('colors.border.selected'), themeGet('colors.background.neutral.subtle.hovered'), themeGet('colors.text.default'), themeGet('colors.background.neutral.subtle.pressed'), themeGet('colors.text.default'), themeGet('colors.elevation.surface.default.[default]'), themeGet('colors.text.disabled'));
const ActionsFilter = ({
  children,
  description,
  errorMessage,
  defaultFilter,
  label,
  items,
  displayCount = false,
  ...props
}) => {
  // iterables aren't mappable
  const copiedItems = [...items];
  const {
    startsWith
  } = useFilter({
    sensitivity: 'base'
  });
  const [filterValue, setFilterValue] = React__default.useState('');
  React__default.useMemo(() => copiedItems.filter(item => startsWith(item.name, filterValue)), [items, filterValue]);

  // Store input value, selected option, open state, and items
  // in a state tracker
  React__default.useState({
    selectedKey: '',
    inputValue: '',
    items: copiedItems
  });
  return /*#__PURE__*/React__default.createElement(StyledActionsFilter, {
    label: label,
    defaultItems: copiedItems,
    inputValue: filterValue,
    onInputChange: setFilterValue,
    allowsCustomValue: true
  }, /*#__PURE__*/React__default.createElement(Combobox.Label, null, label), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex'
    }
  }, /*#__PURE__*/React__default.createElement(StyledActionsFilterInput, null), /*#__PURE__*/React__default.createElement(StyledActionsFilterButton, null, /*#__PURE__*/React__default.createElement(VisuallyHidden, null, "Menu"), /*#__PURE__*/React__default.createElement(AkChevronDownIcon, null))), description && /*#__PURE__*/React__default.createElement(StyledActionsFilterText, {
    slot: "description"
  }, description), errorMessage && /*#__PURE__*/React__default.createElement(StyledActionsFilterText, {
    slot: "errorMessage"
  }, errorMessage), /*#__PURE__*/React__default.createElement(StyledActionsFilterPopover, null, /*#__PURE__*/React__default.createElement(StyledActionsFilterPopover, null, /*#__PURE__*/React__default.createElement(StyledActionsFilterListbox, null, item => /*#__PURE__*/React__default.createElement(StyledComplexActionsFilterItem, {
    textValue: item.displayName,
    label: item.name,
    leadingVisual: item.icon
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      gap: 1
    }
  }, /*#__PURE__*/React__default.createElement(Text, {
    sx: {
      fontWeight: 'medium'
    }
  }, item.displayName), displayCount && /*#__PURE__*/React__default.createElement(Text, null, "(", item.count, ")")))))));
};
ActionsFilter.displayName = "ActionsFilter";
var ActionsFilter$1 = ActionsFilter;

// Complex Combobox Item

export { ComplexComboboxItem, CustomItem, InnerVisualSlot, StyledActionsFilter, StyledActionsFilterButton, StyledActionsFilterInput, StyledActionsFilterListbox, StyledActionsFilterPopover, StyledActionsFilterText, StyledComplexActionsFilterItem, ActionsFilter$1 as default, fontSize };
